/** Buyer-facing copy. Agent-readiness platform PRD 2026-10-20. */

import { AGENT_READINESS_CHECKS } from "~/lib/siteglue/agent-readiness/registry";
import {
  AGENT_READY_CAMPAIGN_H1,
  SITEGLUE_BRAND_EXPRESSION,
  SITEGLUE_HOMEPAGE_H1,
  SITEGLUE_TAGLINE,
} from "~/lib/siteglue/customer-terms";

/** Benchmarks scored on the public audit report. HRO_BENCHMARK_COUNT is a legacy alias. */
export const ARO_BENCHMARK_COUNT = AGENT_READINESS_CHECKS.length;
export const CRO_BENCHMARK_COUNT = 5;
export const HRO_BENCHMARK_COUNT = CRO_BENCHMARK_COUNT;

export const HERO_H1 = SITEGLUE_HOMEPAGE_H1;
export const HERO_H1_BACKUP = SITEGLUE_HOMEPAGE_H1;
export const HERO_EXPRESSION = SITEGLUE_BRAND_EXPRESSION;

export const HERO_SUBHEAD =
  "See how easily people and their AI agents can find, contact, and do business with you—across your website, phone, messaging, and scheduling channels.";

/** On-page homepage hero. SEO title/description use HOMEPAGE_HERO_H1 / HOMEPAGE_HERO_SUPPORT. */
export const HOMEPAGE_HERO_EYEBROW = "SiteGlue Agents";
export const HOMEPAGE_HERO_H1 = AGENT_READY_CAMPAIGN_H1;
export const HOMEPAGE_AUDITS_EYEBROW = "Free AI Optimization Audits";
export const HOMEPAGE_AUDITS_HEADING = SITEGLUE_HOMEPAGE_H1;
export const HOMEPAGE_HERO_SUPPORT =
  "Scan your website to see whether AI recommends your business—and how easily people and their AI agents can get answers, take action, and connect with the right person at your company.";
export const HOMEPAGE_HERO_CHECK_LINE = `${ARO_BENCHMARK_COUNT} ARO checks and ${CRO_BENCHMARK_COUNT} CRO benchmarks.`;
export const RUN_MY_FREE_AUDIT_CTA = "Run My Free Audit";
export const HOMEPAGE_SCAN_CTA = RUN_MY_FREE_AUDIT_CTA;
export const HOMEPAGE_BUILD_CTA = "Build a SiteGlue Agent";
export const HOMEPAGE_URL_LABEL = "Enter your business URL";
export const HOMEPAGE_URL_PLACEHOLDER = "yourcompany.com";
export const HOMEPAGE_PREVIEW_SAMPLE_LABEL = "Sample scores for visual demonstration";
export const HOMEPAGE_PREVIEW_ARO_TITLE = "ARO — Agent Readiness";
export const HOMEPAGE_PREVIEW_AEO_TITLE = "AEO — Answer Engine Optimization";
export const HOMEPAGE_PREVIEW_CRO_TITLE = "CRO — Conversion Optimization";
export const HOMEPAGE_PREVIEW_CHANNELS_TITLE = HOMEPAGE_PREVIEW_CRO_TITLE;
export const HOMEPAGE_PREVIEW_HRO_TITLE = HOMEPAGE_PREVIEW_CRO_TITLE;
export const AUDIT_INDEX_HREF = "/audits";
export const AUDIT_ARO_HREF = "/audits/agent-readiness";
export const AUDIT_CRO_HREF = "/audits/conversion-optimization";
export const AUDIT_AEO_HREF = "/audits/answer-engine-optimization";
export const AUDIT_CHANNELS_HREF = AUDIT_CRO_HREF;
export const AUDIT_AGENTS_TEST_HREF = "/audits/ai-agent-testing";
export const BENCHMARK_LIBRARY_HREF = "/resources/benchmarks";
export const WORDPRESS_PLUGIN_HREF = "/resources/wordpress-plugin";
export const AUDIT_ARO_HREF_LEGACY = "/audits/ai-agent-readiness";
export const AUDIT_CRO_HREF_LEGACY = "/audits/website-conversion";
export const AUDIT_CHANNELS_HREF_LEGACY = "/audits/communication-channels";
export const AUDIT_AGENTS_TEST_HREF_LEGACY = "/audits/test-with-ai-agents";
export const HERO_TRUST_PILL = SITEGLUE_TAGLINE.replace(/\.$/, "");
export const AI_AGENT_CHECKS_LABEL = "AI Agent Readiness Checks:";
export const PEOPLE_CHECKS_LABEL = "CRO Checks:";

export type ScanCheckChip = {
  id: string;
  label: string;
  items: readonly string[];
};

export const AI_AGENT_READINESS_CHIPS: readonly ScanCheckChip[] = [
  {
    id: "discoverability",
    label: "Discoverability",
    items: [
      "robots.txt: Explicitly state which AI bots and crawlers are allowed or blocked using AI bot rules.",
      "sitemap.xml: Publish a clean, comprehensive list of all valid site pages.",
      "Link Headers & Standards: Support RFC link headers and resource discovery manifests like ai-catalog.json.",
    ],
  },
  {
    id: "content_readability",
    label: "Content & Readability",
    items: [
      "Semantic HTML: Use native semantic elements (<header>, <main>, <button>, <article>) and proper ARIA labels so the accessibility tree provides a clear data model for agents.",
      "No-JS Initial Content: Render primary answers and text directly in the initial HTML rather than requiring heavy client-side JavaScript execution.",
      "Markdown Mirrors (llms.txt): Provide lightweight Markdown files or clean text alternatives for fast, low-token parsing.",
      "Structured Data: Implement Schema.org and JSON-LD markup to explicitly define entities, products, and FAQs.",
    ],
  },
  {
    id: "a2a_mcp",
    label: "Agent to Agent MCP Server",
    items: [
      "MCP Server Card: Publish a Model Context Protocol card at /.well-known/mcp.json so another agent can discover tools and call them.",
      "A2A Agent Card: Advertise identity and capabilities for agent-to-agent requests.",
    ],
  },
  {
    id: "bot_access_auth",
    label: "Bot Access Control & Auth",
    items: [
      "Content Signals: Define clear usage permissions for training, search, and data input (ai-train, search, ai-input).",
      "Web Bot Auth: Support edge verification and agent-accessible authentication flows when actions require credentials.",
    ],
  },
  {
    id: "protocol_actions",
    label: "Protocol & Action Capabilities",
    items: [
      "WebMCP: Expose structured, predefined browser tools (such as search, filtering, or cart actions) via the proposed WebMCP standard so agents don’t have to scrape or guess UI layouts.",
      "MCP Server Cards: Expose a Model Context Protocol (MCP) server or card (/.well-known/mcp.json) for programmatic interaction.",
      "Public APIs & Contracts: Publish open OpenAPI specifications, SDKs, or command-line interfaces (CLIs) for direct programmatic tasks.",
    ],
  },
] as const;

export const PEOPLE_READINESS_CHIPS: readonly ScanCheckChip[] = [
  {
    id: "online_support_agent",
    label: "Online Support Agent",
    items: [
      "A chat or voice agent on the website that can answer questions and start a booking or handoff.",
      "Visitors should not have to hunt for a phone number to get help.",
    ],
  },
  {
    id: "rag_knowledge_base",
    label: "RAG Knowledge Base",
    items: [
      "Published facts, services, hours, and policies the agent can retrieve and quote accurately.",
      "Answers should come from your content, not a generic model guess.",
    ],
  },
  {
    id: "multi_channel_access",
    label: "Multi-Channel Access",
    items: [
      "People can reach the business by chat, phone, form, or booking — not a single buried contact path.",
      "The same request should work across the channels you offer.",
    ],
  },
  {
    id: "hitl_architecture",
    label: "Human In the Loop Architecture",
    items: [
      "A clear path to a real teammate when the agent cannot complete the request.",
      "Human-in-the-Loop™ stays in control of exceptions, approvals, and sensitive decisions.",
    ],
  },
  {
    id: "after_hours_support_agent",
    label: "After Hours Support Agent",
    items: [
      "An agent that answers when the office is closed so visitors are not left with a voicemail or empty chat.",
      "After-hours requests can book, take a message, or escalate to the on-call teammate.",
    ],
  },
] as const;

/** Trish /start and signed-in hire. Not the homepage scan button. */
export const PRIMARY_CTA = "Get Started";
export const SCAN_CTA = RUN_MY_FREE_AUDIT_CTA;
export const NAV_PRIMARY_CTA = RUN_MY_FREE_AUDIT_CTA;
export const AGENT_READY_CTA = "Scan My Business";
export const AUDIT_HERO_H1 = AGENT_READY_CAMPAIGN_H1;
export const AUDIT_HERO_SUBHEAD = HERO_SUBHEAD;
export const RESULTS_PRIMARY_CTA = "Fix with SiteGlue";
export const RESULTS_PRIMARY_CTA_COUNTED = (n: number) =>
  `Fix ${n} issue${n === 1 ? "" : "s"} with SiteGlue`;
export const RESULTS_SIDEBAR_TITLE = "Improve your site with SiteGlue";
export const RESULTS_VERIFIED_GAPS = (n: number) =>
  `${n} verified gap${n === 1 ? "" : "s"}`;
export const RESULTS_ITEMS_TO_VERIFY = (n: number) =>
  `${n} item${n === 1 ? "" : "s"} to verify`;
export const RESULTS_INSUFFICIENT = "Insufficient evidence";
export const RESULTS_COVERAGE = (pct: number) =>
  `${pct}% of applicable CRO checks were conclusive`;
export const RESULTS_CHANNEL_COVERAGE = (pct: number) =>
  `${pct}% of applicable channel checks were conclusive`;
export const RESULTS_CRO_BENCHMARKS_SCORED = (n: number) =>
  `${n} of 5 CRO benchmarks scored`;
export const RESULTS_SCORE_HELP =
  "Scores use only conclusive checks. Detected is not the same as tested end to end.";
export const DOWNLOAD_AUDIT = "Full Report";
export const RESULTS_EMAIL_CTA = "Email report";
export const FULL_REPORT_TITLE = "Get the full report";
export const FULL_REPORT_BODY =
  "A detailed, shareable report of your ARO and CRO scores, the impact of each gap, and prioritized recommendations.";
export const FULL_REPORT_AUDIENCE_LABEL = "Who is it for?";
export const FULL_REPORT_GENERATE = "Generate";
export const FULL_REPORT_EMAIL_HINT =
  "Any email works. We email you the formatted PDF and a link you can share.";
export const FULL_REPORT_SENT =
  "Sent. Check your inbox for the PDF report. We also saved a copy to your downloads.";
export const FULL_REPORT_AUDIENCES = [
  { id: "product", label: "Product" },
  { id: "engineering", label: "Engineering" },
  { id: "marketing", label: "Marketing" },
] as const;
export const RESULTS_BACK = "Back to scan";
export const RESULTS_TITLE = (name: string) =>
  `ARO | CRO Readiness report for ${name}`;
export const SCAN_OPENING_TITLE = "Starting your scan";
export const SCAN_OPENING_BODY =
  "We are opening your ARO and CRO report. This page will update when the scan is ready.";
export const RESULTS_PEOPLE_LABEL = "CRO Audit";
export const RESULTS_PEOPLE_SUMMARY =
  "How well visitors convert: first impression, CTAs, trust, mobile, and conversion flow across your channels.";
export const RESULTS_AGENTS_LABEL = "ARO Audit";
export const RESULTS_AGENTS_SUMMARY =
  "How well an external AI agent can discover, understand, authenticate, and interact with this business.";
export const RESULTS_CHANNELS_LABEL = "Channel findings";
export const RESULTS_CHANNELS_SUMMARY =
  "Forms, live chat, browser calls, phone, SMS, and email mapped into the CRO benchmarks. Not a separate score.";
export const DIRECTORY_SCORE_AGENT = "ARO";
export const DIRECTORY_SCORE_CONVERSION = "CRO";
export const DIRECTORY_SCORE_AEO = "AEO";
export const DIRECTORY_SCORE_CHANNELS = "CRO";
export const DIRECTORY_SCORE_NOT_VERIFIED = "Not yet verified";
export const DIRECTORY_LEGACY_PEOPLE = "Previous People score";
export const DIRECTORY_LEGACY_CHANNELS = "Previous Channel score";
export const CRO_VIEW_BENCHMARK = "Benchmark";
export const CRO_VIEW_CHANNEL = "Channel";
export const CRO_VIEW_FLOW = "Conversion Flow";
export const RESULTS_IMPROVE_ITEM = "Improve Your Score";
export const RESULTS_OPPORTUNITY = "Recommended";
export const IMPROVE_FILTER_ALL = "All Improvements";
export const IMPROVE_FILTER_QUICK = "Quick Wins";
export const IMPROVE_FILTER_IMPACT = "Highest Impact";
export const IMPROVE_FILTER_VERIFY = "Needs Verification";
export const IMPROVE_PROJECTION_CAPTION =
  "Potential score after selected fixes are implemented and verified.";
export const IMPROVE_NEEDS_ASSESSMENT = "Needs Assessment";
export const IMPROVE_EMPTY_QUICK = "No Quick Wins on this scan. Try Highest Impact or All Improvements.";
export const IMPROVE_EMPTY_VERIFY = "Nothing is waiting on verification.";
export const IMPROVE_TEST_AGAIN = "Test Again";
export const IMPROVE_PLAN_ALL = "All Selected";
export const IMPROVE_PLAN_QUICK = "Quick Wins First";
export const IMPROVE_PLAN_PHASED = "Phased Plan";
export const COMPARE_CAVEAT_METHOD = "Method or rubric changed. Do not treat the score delta as causal lift.";
export const WALK_TASK_HEADING = "Task outcome";
export const WALK_TASK_VERIFIED = "Verified";
export const WALK_TASK_NOT_COMPLETED = "Not Completed";
export const WALK_TASK_LIMITATIONS = "Limitations";
export const WALK_TASK_NEXT = "Next Step";
export const ROADMAP_TITLE = "Capability roadmap";
export const ROADMAP_HELP =
  "Evidence-based stages from this scan. Percent complete is not shown until a level is verified.";
export const MILESTONE_INTERNAL_NOTE =
  "Business Agent-Ready criteria are internal. No public badge is shown until the criteria are approved.";
export const RESULTS_TAB_ALL = "All Benchmarks";
export const RESULTS_TAB_PEOPLE = "CRO Audit";
export const RESULTS_TAB_AGENTS = "ARO Audit";
export const RESULTS_TAB_AEO = "AEO Audit";
export const RESULTS_AEO_LABEL = "AEO Audit";
export const RESULTS_AEO_SUMMARY =
  "Answer Engine Optimization. Technical entity and content signals from your site. Answer-engine visibility stays Not verified until a live labeled test runs.";
export const RESULTS_TAB_CHANNELS = "Channel findings";
export const REPORT_TAB_AGENTS_TEST = "Test With AI Agents";
export const REPORT_TAB_WATCH = "Watch an Agent";
export const REPORT_TAB_IMPROVE = "Improve Your Score";
export const RESULTS_EVIDENCE_STATUS = "Evidence status";
export const RESULTS_EVIDENCE_VERIFIED = "SiteGlue Agent response verified";
export const RESULTS_EVIDENCE_DISCOVERY = "Technical discovery only";
export const RESULTS_EVIDENCE_UNVERIFIED = "Not verified";
export const RESULTS_EVIDENCE_NOT_SELECTED = "Not selected";
export const WALK_EYEBROW = "See how top personal AI agents use your site to get information and take action";
export const WALK_BUTTON = "Run";
export const WALK_SIDEBAR_BUTTON = WALK_BUTTON;
export const WALK_LEGACY_EYEBROW = "Watch an agent use your site";
export const WALK_INTENT = "The question";
export const WALK_STEPS_LABEL = "Steps";
export const WALK_DURATION_LABEL = "Duration";
export const WALK_TOKENS_LABEL = "Tokens";
export const WALK_ANSWER_FROM_SITE = "Answer from your site";
export const WALK_ANSWER_EFFICIENCY = "Answer efficiency";
export const WALK_FOLLOWED_LINKS = "Followed site links";
export const WALK_ANSWER_SOURCES = "Answer sources";
export const WALK_FROM_THIS_SITE = "from this site";
export const WALK_FROM_EXTERNAL = "from external pages";
export const WALK_FROM_SEARCH = "from search results";
export const WALK_FROM_AGENT_KNOWLEDGE = "from agent knowledge";
export const WALK_URL_DISCOVERY = "URL discovery";
export const WALK_GIVEN_IN_TASK = "given in the task";
export const WALK_FOLLOWED_A_LINK = "followed a link";
export const WALK_INSIGHT = "Insight";
export const WALK_ANSWER_HEADING = (agentLabel: string) => `${agentLabel} answer`;
export const WALK_SITEGLUE_BENCHMARKS = "SiteGlue benchmarks";
export const WALK_FOUND_MCP = "Found MCP server";
export const WALK_IDENTIFIED_SELF = "Identified self";
export const WALK_RUN_AGAIN = "Run again";
export const WALK_RUNNING = "A Claude Duet model on SiteGlue is reading public pages…";
export const WALK_CANCEL = "Cancel";
export const WALK_FIX_GAP = "Fix this gap with SiteGlue";
export const WALK_COPY_PROMPT = "Copy Fix Prompt";
export const WALK_GET_FIXED = "Get it fixed for you";
export const WALK_CLOSE = "Close";
export const WALK_QUESTION_LABEL = "Question";
export const WALK_AGENT_LABEL = "Personal AI agent";
export const WALK_HELPER =
  "Choose a question and an agent. Claude Duet or Grok can walk public pages when a model key is available. OpenClaw, Perplexity, Muse, and Grok without a key use a SiteGlue scan simulation. That is not the consumer app. It will not submit forms.";
export const WALK_HELPER_LIVE =
  "A model on SiteGlue is reading public pages now. This is not the consumer app. It will not submit forms.";

export function walkHelperForAgent(label: string, mode: "live" | "scan"): string {
  if (mode === "live") {
    return `A ${label} model on SiteGlue is reading public pages now. This is not the ${label} consumer app. It will not submit forms.`;
  }
  return `This is a SiteGlue scan simulation for ${label}. SiteGlue does not have an authorized ${label} consumer runtime. It will not submit forms.`;
}

export function walkRunningForAgent(label: string, mode: "live" | "scan"): string {
  if (mode === "scan") return `SiteGlue is simulating ${label} from pages already scanned…`;
  return `A ${label} model on SiteGlue is reading public pages…`;
}

export const WALK_OUTCOME_LIVE = "Live test";
export const WALK_OUTCOME_SIMULATION = "Simulation";
export const WALK_OUTCOME_UNSUPPORTED = "Unsupported";
export const WALK_OUTCOME_INCONCLUSIVE = "Inconclusive";
export const WALK_OUTCOME_LIVE_HELP =
  "A model on SiteGlue ran this walk. This is not the consumer app.";
export const WALK_OUTCOME_SIMULATION_HELP =
  "SiteGlue simulated this agent from pages already scanned. It did not use the consumer app.";
export const WALK_OUTCOME_UNSUPPORTED_HELP =
  "SiteGlue does not have an authorized runtime for this named consumer agent.";
export const WALK_OUTCOME_INCONCLUSIVE_HELP =
  "The walk failed, was cancelled, or did not fetch enough pages to judge the task.";
export const SCAN_SKIP_SELECTION = "Skip selection / website-only scan";
export const SCAN_AGENTS_LABEL = "What personal AI Agents do you want to check?";
export const SCAN_REPORTS_LABEL = "What do you want to measure?";
export const SCAN_REPORT_ARO_TITLE = "ARO Audit";
export const SCAN_REPORT_ARO_HELP =
  "Agent Readiness Optimization. Crawl rules, structured knowledge, MCP, and whether an external agent can act.";
export const SCAN_REPORT_CRO_TITLE = "CRO Audit";
export const SCAN_REPORT_CRO_HELP =
  "Conversion Rate Optimization. First impression, CTAs, trust, mobile, and conversion flow across forms, chat, calls, SMS, and email.";
export const SCAN_REPORT_AEO_TITLE = "AEO Audit";
export const SCAN_REPORT_AEO_HELP =
  "Answer Engine Optimization. How AI answer engines could discover, cite, and recommend this business. Live ChatGPT or Gemini recommendations are never claimed without a labeled test.";
export const AUDIT_ARO_CTA = "Run My Free ARO Audit";
export const AUDIT_CRO_CTA = "Run My Free CRO Audit";
export const AUDIT_AEO_CTA = "Run My Free AEO Audit";
export const AUDIT_ARO_HEADLINE = "Is Your Website Ready for AI Agents?";
export const AUDIT_CRO_HEADLINE = "Is Your Website Turning Visitors Into Customers?";
export const AUDIT_AEO_HEADLINE = "Will AI Recommend Your Business?";
export const CUSTOMIZE_AUDIT_LABEL = "Customize Audit";
export const ACTIVATE_WITH_SITEGLUE_CTA = "Activate With SiteGlue";
export const REQUEST_ACTIVATION_CTA = "Request activation";
export const AUDIT_CHOOSER_LABEL = "Choose your audit";
export const AUDIT_CHOOSER_DEFAULT = "aro" as const;
export const AUDIT_CHOOSER_OPTIONS = [
  { id: "aro", label: "ARO — AI Agent Readiness", href: AUDIT_ARO_HREF },
  { id: "aeo", label: "AEO — AI Search Visibility", href: AUDIT_AEO_HREF },
  { id: "cro", label: "CRO — Website Conversion", href: AUDIT_CRO_HREF },
] as const;
export const SITEGLUE_PRIMARY_AUDIT_NAV = [
  { label: "ARO Audit", href: AUDIT_ARO_HREF },
  { label: "AEO Audit", href: AUDIT_AEO_HREF },
  { label: "CRO Audit", href: AUDIT_CRO_HREF },
] as const;
export type AuditChooserId = (typeof AUDIT_CHOOSER_OPTIONS)[number]["id"];
export const SCAN_REPORT_HRO_TITLE = SCAN_REPORT_CRO_TITLE;
export const SCAN_REPORT_HRO_HELP = SCAN_REPORT_CRO_HELP;
export const SCAN_REPORT_CHANNELS_TITLE = SCAN_REPORT_CRO_TITLE;
export const SCAN_REPORT_CHANNELS_HELP = SCAN_REPORT_CRO_HELP;
export const SCAN_REPORT_AGENTS_TEST_TITLE = "Test With AI Agents";
export const SCAN_REPORT_AGENTS_TEST_HELP =
  "Optional. Run a live model or a scan simulation against a task. This does not change your audit scores.";
export const SCAN_WALK_TASKS_LABEL = "Tasks to test";
export const SCAN_WIZARD_NEED_REPORT = "Select at least one report.";
export const SCAN_CHANNELS_LABEL = "Which conversion channels should we evaluate?";
export const SCAN_EXAMPLE_URL = "https://orchardparkhealthcare.com/";
export const SCAN_EXAMPLE_HINT = "Try a sample scan";
export const SCAN_WIZARD_CONTINUE = "Continue";
export const SCAN_WIZARD_BACK = "Back";
export const SCAN_WIZARD_STEP_WEBSITE = "Website";
export const SCAN_WIZARD_STEP_REPORTS = "Reports";
export const SCAN_WIZARD_STEP_AGENTS = "Agents";
export const SCAN_WIZARD_STEP_CHANNELS = "Channels";
export const SCAN_WIZARD_STEP_TOOLS = "Tools";
export const SCAN_WIZARD_AGENTS_HELP = "Check one or more personal AI agents. We will assess how they can find and use this business.";
export const SCAN_WIZARD_CHANNELS_HELP =
  "Test if people and their personal AI Agents have access to the following communication channels.";
export const SCAN_WIZARD_NEED_AGENT = "Check at least one personal AI agent.";
export const SCAN_WIZARD_NEED_CHANNEL = "Check at least one channel.";
export const SCAN_WIZARD_NEED_SCHEDULE = "Choose the booking tool you use.";
export const SCAN_WIZARD_NEED_EMR = "Choose the EMR platform you use.";
export const SCAN_SCHEDULE_LABEL = "Booking tool";
export const SCAN_TOOLS_LABEL = "Enable to select your online booking system or EMR.";
export const SCAN_TOOLS_HELP = "Enable to select your online booking system or EMR.";
export const SCAN_EMR_LABEL = "EMR platform";
export const SCAN_NOW = "Scan Now";
export const CHANNEL_MATRIX_TITLE = "Channel readiness";
export const DOWNLOAD_CODING_PACKAGE = "Download coding-agent package";
export const WALK_OUTCOME = "Scenario outcome";
export const WALK_DIAGNOSTICS = "Walk diagnostics";
export const WALK_METRICS_NOTE =
  "These percentages are diagnostic step counts from this walk, not measured traffic. from this site is 100 when pages on this domain were fetched. Cost is omitted unless a billed amount is recorded.";
export const PHONE_CONSENT_BUTTON = "Record phone-test consent";
export const PHONE_CONSENT_HELP =
  "Consent is required before any outbound phone test. SiteGlue will not dial a production number from this public scan.";
export const RESULTS_STATUS = (score: number | null) => {
  if (score == null) return "Not verified";
  if (score >= 70) return "Ready";
  if (score >= 40) return "Partly ready";
  return "Not ready yet";
};
export const RESULTS_NOT_VERIFIED = "Not verified";
export const RESULTS_SHARE = "Share";
export const RESULTS_SHARE_COPIED = "Link copied";
export const RESULTS_RESCAN = "Rescan";
export const RESULTS_EXPORT_PDF = "PDF";
export const RESULTS_EXPORT_CSV = "CSV";
export const RESULTS_OPEN_SITE = "Open website";
export const RESULTS_SHOW_SITE = "Show website";
export const RESULTS_HIDE_SITE = "Hide website";
export const RESULTS_RUBRIC = (agent: string, people: string, channels: string) =>
  `Rubric · ARO ${agent} · CRO ${people} · Channels ${channels}`;
export const IMPROVE_CONNECT = "How to fix customer engagement and optimize conversions (CRO)";
export const IMPROVE_ENABLE = "How to fix Agent-Readiness";
export const IMPROVE_CONVERT = "Convert More Inquiries";
export const IMPROVE_GROUP_ORDER = ["enable", "connect", "convert"] as const;
export const IMPROVE_COPY_PROMPT = "Copy Fix Prompt";
export const IMPROVE_PROMPT_COPIED = "Prompt copied";
export const IMPROVE_DOWNLOAD_ZIP = "Fix Prompts";
export const IMPROVE_SELECTED = (n: number) => `${n} selected`;
export const IMPROVE_SELECT_HELP =
  "Check the items to include in Fix Prompts or Talk to us. If none are checked, all items are included.";
export const PATH_CODING_TITLE = "Use my coding agent";
export const PATH_CODING_OFFER = "Copy reviewable prompts";
export const PATH_CODING_BUTTON = "Fix Prompts";
export const PATH_CODING_HELP =
  "Open prompts for Cursor, Claude Code, and Codex. Review before you apply any write.";
export const FIX_PROMPTS_TITLE = "Fix Prompts";
export const FIX_PROMPTS_HELP =
  "Copy a prompt for your coding agent. Review the proposal before applying any write.";
export const PATH_TRISH_TITLE = "Build with Trish";
export const PATH_TRISH_HELP =
  "Website fixes are optional. Trish can still set up the SiteGlue Agent from your website and selected channels.";
export const PATH_MANAGED_TITLE = "I'd rather you handle it";
export const PATH_SELF_TITLE = "I'll fix it myself";
export const PATH_SELF_OFFER = "Use the platform";
export const PATH_SELF_BUTTON = "Start with Trish";
export const PATH_HELP_TITLE = "I'd rather you handle it";
export const PATH_HELP_OFFER = "We do it for you";
export const PATH_HELP_BUTTON = "Talk to us";
export const PLATFORM_H2 =
  "The agent-readiness platform: scan, audit, fix, and deploy in minutes";
export const SECONDARY_CTA = "Set Up Your Agent";
export const DEMO_FORM_TITLE = (domain: string) =>
  `Let's talk about getting ${domain} agent-ready`;
export const DEMO_FORM_SUBMIT = "Submit";
export const LOGGED_IN_CTA = "Open my Agent";
export const TRUST_LINE =
  "Free scan · 15-day trial when you deploy · Always Human-in-the-Loop™";

export const URL_PLACEHOLDER = "Enter your website URL";
export const URL_LABEL = "Your business website URL";

export const SIGNUP_HEADLINE =
  "Create your SiteGlue account so Trish can save your agent setup. Your 15-day trial starts after you add a card.";

export const CONSENT_LINE =
  "Email me updates about my agent (install help, alerts, tips). Unsubscribe anytime.";

export const BOOK_HEADLINE = "Let's set up your AI agent in 15 minutes.";

export const PT_H1 = "Set up a SiteGlue Agent for your PT clinic.";
export const PT_SUBHEAD =
  "Trish scans your clinic and configures a SiteGlue Agent for intake questions, eval bookings, and after-hours coverage — always with a Human-in-the-Loop™.";

export const PT_VARIANT_B_SUBHEAD =
  "Tell us about your clinic. In 15 minutes we'll configure a SiteGlue Agent for intake questions, eval bookings, and after-hours coverage — always with a Human-in-the-Loop™.";
